// PostHog client — lazy singleton, ported from atlas-mobile's
// src/utils/posthog.ts so web and mobile share one project, one event
// taxonomy, and one feature-flag space.
//
// Dual-fire transition: GA4 remains the primary sink (see gtag.ts); PostHog
// fires in parallel via trackEvent/AnalyticsProvider. PostHog adds what GA
// can't do: feature flags, session replay, and high-cardinality properties
// (per-customer ids would blow GA's custom-dimension limits). GA can be
// retired once PostHog proves out.
//
// Disabled in development (mirrors GA) and when no key is configured — every
// call site must tolerate null. The key is a public client token; bundling a
// default mirrors atlas-mobile's app.config.ts so forks/pre-setup builds
// stay safe via the env override.
import { posthogApiKey } from '@/utils/analytics/posthog-key'
import { redactUrlsIn } from '@/utils/analytics/redact-url'
import { publicEnv } from '@/utils/public-env'
import { appVersion } from '@/utils/version'

import posthogJs, { type CaptureResult, type PostHog } from 'posthog-js'

import { getAppEnvironment } from './app-environment'

const API_KEY: string = posthogApiKey()
// Relative by default: PostHog traffic rides our own origin through the
// /ingest rewrite in next.config.ts, so tracker blockers that kill
// *.posthog.com by hostname can't take feature flags down with it (they
// fall back to their prod default = false, i.e. the feature silently
// vanishes for that user). The env override remains the escape hatch for
// pointing a build straight at PostHog.
const HOST: string = publicEnv('NEXT_PUBLIC_POSTHOG_HOST') ?? '/ingest'

let client: PostHog | null = null
let initialized = false

function isEnabled(): boolean {
  if (typeof window === 'undefined') return false
  if (process.env.NODE_ENV === 'development') return false
  return API_KEY.length > 0
}

/** rrweb full and incremental snapshots: the DOM itself, not its address. */
const DOM_SNAPSHOT_TYPES = new Set([2, 3])

/**
 * PostHog stamps the address bar on every event — `$current_url`,
 * `$pathname`, `$referrer`, their `$initial_*` / `$session_entry_*` copies —
 * and a session recording carries it again in its meta and network events, so
 * a link secret in the URL would ride along on all of them. Matches by value,
 * never by key: PostHog's own `token` property is the project key it needs.
 * Throwing here would surface in capture() at the call site, so a payload
 * that cannot be redacted is dropped instead.
 */
function redactLinkSecrets(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event
  try {
    const { $snapshot_data: recording, ...properties } = event.properties
    redactUrlsIn(properties)
    for (const item of [recording].flat()) {
      if (!DOM_SNAPSHOT_TYPES.has((item as { type?: number })?.type ?? -1)) {
        redactUrlsIn(item)
      }
    }
    event.properties =
      recording === undefined
        ? properties
        : { ...properties, $snapshot_data: recording }
    redactUrlsIn(event.$set)
    redactUrlsIn(event.$set_once)
    return event
  } catch {
    return null
  }
}

export function getPostHog(): PostHog | null {
  if (initialized) return client
  if (!isEnabled()) return null

  initialized = true
  client = posthogJs.init(API_KEY, {
    api_host: HOST,
    // With api_host now a first-party proxy path, links to the PostHog app
    // (toolbar, authenticated surfaces) still need the real UI origin.
    ui_host: 'https://us.posthog.com',
    // AnalyticsProvider owns the navigation stream (App Router pathname
    // changes), same as it does for GA — the auto-capture hooks predate the
    // App Router and miss soft navigations.
    capture_pageview: false,
    capture_pageleave: true,
    // Explicit events only — the taxonomy is shared with atlas-mobile, and
    // autocapture would drown the ~30 deliberate events in click noise.
    // Session replay stays remote-controlled from the PostHog project
    // settings, so it can be turned on without a deploy.
    autocapture: false,
    persistence: 'localStorage+cookie',
    before_send: redactLinkSecrets
  })

  // Super-properties stamped on every event (parallel to mobile's
  // build_variant): which build and which environment produced it.
  client.register({
    app_version: appVersion,
    app_environment: getAppEnvironment(),
    platform: 'web'
  })

  return client
}

// Test seam, mirroring atlas-mobile. No consumer until tests land.
// fallow-ignore-next-line unused-export
export function setPostHogForTesting(instance: PostHog | null): void {
  client = instance
  initialized = instance !== null
}
